/**
 * Renewals held as possible duplicate accounts (payments/duplicate_guard.py).
 *
 * The engine did not charge these: the member seems to have another live membership on the
 * same title (same email, or same name and address). Nothing was taken from their card. Staff
 * decide: Charge anyway (two people, or the other one should go), Turn off auto-renew (a
 * duplicate; it runs to its end date), or merge the two accounts on Duplicates. Undecided past
 * the grace period, the held membership ends like a failed renewal.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../api/client'
import { PubBadge } from '../components/PubBadge'
import { fmtMoney } from '../lib/format'

type Side = {
  subscription_id: number | null
  subscriber_id: number | null
  name: string
  account_number: string | null
  email: string | null
  current_period_end: string | null
  auto_renew: boolean
  card_last4: string | null
}

type Hold = {
  id: number
  publication_slug: string
  period_end: string
  reason: string
  amount_cents: number | null
  status: string
  created_at: string
  resolved_at: string | null
  resolved_by: string | null
  held: Side
  twin: Side
}

type Holds = { open: Hold[]; recent: Hold[] }

const STATUS_LABEL: Record<string, string> = {
  charge_anyway: 'charged anyway',
  auto_renew_off: 'auto-renew turned off',
  merged: 'merged',
  lapsed: 'ended, no decision',
  cleared: 'cleared, other account gone',
}

export function reasonLabel(r: string): string {
  return r === 'same_email' ? 'same email' : 'same name and address'
}

function Person({ s }: { s: Side }) {
  if (!s.subscriber_id) return <span className="text-paper-dim">{s.name}</span>
  return (
    <div>
      <Link to={`/subscribers/${s.subscriber_id}`} className="text-paper hover:text-amber-glow hover:underline">
        {s.name}
      </Link>
      <div className="text-paper-trace text-[11px] font-mono">
        {s.account_number}{s.email ? ` · ${s.email}` : ''}
      </div>
      <div className="text-paper-dim text-[11px] font-mono">
        ends {s.current_period_end ?? '—'} · {s.auto_renew ? 'auto-renew on' : 'auto-renew off'}
        {s.card_last4 ? ` · card ${s.card_last4}` : ''}
      </div>
    </div>
  )
}

export function RenewalHolds() {
  const qc = useQueryClient()
  const [note, setNote] = useState<string | null>(null)
  const { data } = useQuery({
    queryKey: ['renewal-holds'],
    queryFn: () => api.get<Holds>('/api/renewals/holds'),
    refetchInterval: 60_000,
  })
  const act = useMutation({
    mutationFn: ({ id, what }: { id: number; what: 'charge' | 'stop' }) =>
      api.post<{ message: string }>(`/api/renewals/holds/${id}/${what}`, {}),
    onSuccess: (r) => {
      setNote(r.message)
      qc.invalidateQueries({ queryKey: ['renewal-holds'] })
      qc.invalidateQueries({ queryKey: ['renewals-overview'] })
    },
    onError: (e: Error) => setNote(e.message),
  })
  if (!data) return null

  return (
    <section className="space-y-2" id="renewal-holds">
      <h2 className="label">Held · possible duplicate accounts ({data.open.length})</h2>
      <p className="text-sm text-paper-dim max-w-[80ch]">
        Not charged: the member seems to have another live membership on the same title. Nothing was
        taken from their card. If no one decides, the held membership ends after the grace period.
      </p>
      {note && <p className="font-mono text-[12px] text-amber-glow">{note}</p>}
      {data.open.length === 0 ? (
        <p className="text-sm text-paper-dim">Nothing held.</p>
      ) : (
        <div className="border border-ink-500 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <th className="label py-2 px-4 font-normal text-left">Held membership</th>
                <th className="label py-2 px-4 font-normal text-left">Title</th>
                <th className="label py-2 px-4 font-normal text-left">Due</th>
                <th className="label py-2 px-4 font-normal text-left">Looks like</th>
                <th className="label py-2 px-4 font-normal text-left">Why</th>
                <th className="label py-2 px-4 font-normal text-left">Decide</th>
              </tr>
            </thead>
            <tbody>
              {data.open.map((h) => (
                <tr key={h.id} className="border-b border-ink-700/40 align-top">
                  <td className="py-2 px-4 text-[13px]"><Person s={h.held} /></td>
                  <td className="py-2 px-4"><PubBadge slug={h.publication_slug} /></td>
                  <td className="py-2 px-4 font-mono text-[13px] text-paper-dim">
                    {h.period_end}{h.amount_cents ? ` · ${fmtMoney(h.amount_cents / 100, false, 2)}` : ''}
                  </td>
                  <td className="py-2 px-4 text-[13px]"><Person s={h.twin} /></td>
                  <td className="py-2 px-4 font-mono text-[12px] text-paper-dim">{reasonLabel(h.reason)}</td>
                  <td className="py-2 px-4 text-[12px] space-y-1">
                    <button type="button" disabled={act.isPending}
                      className="block label-amber hover:underline"
                      onClick={() => { if (confirm(`Charge ${h.held.name} now?`)) act.mutate({ id: h.id, what: 'charge' }) }}>
                      Charge anyway
                    </button>
                    <button type="button" disabled={act.isPending}
                      className="block label hover:underline"
                      onClick={() => act.mutate({ id: h.id, what: 'stop' })}>
                      Turn off auto-renew
                    </button>
                    <Link to="/duplicates" className="block label hover:underline">Merge on Duplicates</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data.recent.length > 0 && (
        <details className="text-[12px] text-paper-dim">
          <summary className="label cursor-pointer">Decided in the last 30 days ({data.recent.length})</summary>
          <ul className="mt-2 space-y-1 font-mono">
            {data.recent.map((h) => (
              <li key={h.id}>
                {h.held.name} ({h.held.account_number}) · {h.publication_slug} · {h.period_end} ·{' '}
                {STATUS_LABEL[h.status] ?? h.status}{h.resolved_by ? ` · ${h.resolved_by}` : ''}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}
